import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, GraduationCap, UsersThree, PencilSimple, Trash, Certificate } from "@phosphor-icons/react";
import { fmtDate } from "../../lib/dateUtil";
import { useBrandedPdf } from "./useBrandedPdf";
import OperativePicker from "../hs/OperativePicker";
import CourseCreateDialog from "./CourseCreateDialog";
import CourseEditorDialog from "./CourseEditorDialog";

const STATUS_TONE = { draft: "bg-slate-100 text-slate-700", published: "bg-emerald-100 text-emerald-800", archived: "bg-slate-100 text-slate-500" };
const REC_TONE = { assigned: "bg-slate-100 text-slate-700", in_progress: "bg-sky-100 text-sky-800", passed: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-700" };

function Pill({ label, value, tone = "", testId }) {
  return (
    <div className={`bg-card border border-border rounded-md px-3 py-2 ${tone}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="text-xl font-[Chivo] font-bold leading-tight">{value}</p>
    </div>
  );
}

function AssignDialog({ course, open, onOpenChange, onDone }) {
  const [assigned, setAssigned] = useState([]);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAssigned([]); setDue(""); } }, [open]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hse/training/courses/${course.id}/assign`, { assigned, due_date: due });
      toast.success(`Assigned to ${data.assigned.length} operative${data.assigned.length === 1 ? "" : "s"}`); onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!course) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="course-assign-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Assign “{course.title}”</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Operatives</Label><OperativePicker value={assigned} onChange={setAssigned} testPrefix="course-assign-picker" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Complete by (optional)</Label><DateInput value={due} onChange={e => setDue(e.target.value)} className="w-44" data-testid="course-assign-due" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={go} disabled={busy || !assigned.length} data-testid="course-assign-btn">Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Staff training hub: courses (AI-written, publish, assign) and the training record of every operative. */
export default function TrainingPanel() {
  const [courses, setCourses] = useState([]);
  const [rec, setRec] = useState(null);
  const [view, setView] = useState("courses");
  const [create, setCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [assign, setAssign] = useState(null);

  const load = useCallback(() => {
    API.get("/hse/training/courses").then(({ data }) => setCourses(data.courses || [])).catch(() => {});
    API.get("/hse/training/records").then(({ data }) => setRec(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!courses.some(c => c.ai?.status === "pending")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [courses, load]);

  const del = async (c) => {
    if (!window.confirm(`Delete “${c.title}”?`)) return;
    try { await API.delete(`/hse/training/courses/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const { openBranded, brandingDialog } = useBrandedPdf();
  const cert = (r) => openBranded({ url: `/in-house-portal/training/${r.id}/certificate.pdf`, filename: `${r.certificate_no}.pdf`, title: `Certificate ${r.certificate_no} branding` });
  const delRec = async (r) => {
    try { await API.delete(`/hse/training/assignments/${r.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="training-panel">
      {rec && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="training-summary">
          <Pill label="Courses live" value={courses.filter(c => c.status === "published").length} testId="training-stat-live" />
          <Pill label="Outstanding" value={rec.summary.outstanding} testId="training-stat-outstanding" />
          <Pill label="Overdue" value={rec.summary.overdue} tone={rec.summary.overdue ? "border-red-300" : ""} testId="training-stat-overdue" />
          <Pill label="Passed" value={rec.summary.passed} testId="training-stat-passed" />
          <Pill label="Expiring in 60d" value={rec.summary.expiring_soon} tone={rec.summary.expiring_soon ? "border-amber-300" : ""} testId="training-stat-expiring" />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 border border-border rounded-md p-0.5">
          {[["courses", "Courses"], ["records", "Training records"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)} className={`px-3 py-1 text-xs rounded ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid={`training-view-${k}`}>{l}</button>
          ))}
        </div>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setCreate(true)} data-testid="training-new-course-btn"><Plus size={14} className="mr-1" /> New course (AI)</Button>
      </div>

      {view === "courses" && (
        <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="training-courses">
          {courses.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center" data-testid="training-courses-empty">No courses yet — pick a topic and the AI writes the modules and quiz in about a minute.</p>}
          {courses.map(c => (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`training-course-${c.id}`}>
              <GraduationCap size={20} className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.title}</p>
                <p className="text-[11px] text-muted-foreground truncate">{c.topic_label} · {c.module_count} modules · {c.question_count} questions · pass {c.pass_mark}% · {c.validity_months ? `valid ${c.validity_months} months` : "no expiry"}</p>
              </div>
              {c.ai?.status === "pending" && <Badge className="bg-amber-100 text-amber-800 animate-pulse text-[10px]">AI writing…</Badge>}
              <Badge className={`text-[10px] capitalize ${STATUS_TONE[c.status]}`} data-testid={`training-course-status-${c.id}`}>{c.status}</Badge>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => setEditId(c.id)} data-testid={`training-course-edit-${c.id}`}><PencilSimple size={13} className="mr-1" /> {c.status === "draft" ? "Review & publish" : "Open"}</Button>
              {c.status === "published" && <Button size="sm" className="h-7" onClick={() => setAssign(c)} data-testid={`training-course-assign-${c.id}`}><UsersThree size={13} className="mr-1" /> Assign</Button>}
              {c.status !== "published" && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => del(c)} data-testid={`training-course-del-${c.id}`}><Trash size={13} /></Button>}
            </div>
          ))}
        </div>
      )}

      {view === "records" && rec && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="training-records">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground"><tr>
              <th className="text-left px-3 py-2">Operative</th><th className="text-left px-3 py-2">Course</th><th className="text-left px-3 py-2">Assigned</th><th className="text-left px-3 py-2">Due</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Score</th><th className="text-left px-3 py-2">Certificate</th><th />
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rec.records.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground" data-testid="training-records-empty">Nobody has been assigned a course yet.</td></tr>}
              {rec.records.map(r => (
                <tr key={r.id} data-testid={`training-record-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2">{r.course_title}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.assigned_at)}</td>
                  <td className={`px-3 py-2 ${r.overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>{r.due_date ? fmtDate(r.due_date) : "—"}</td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] ${REC_TONE[r.status]}`}>{r.status.replace("_", " ")}{r.overdue ? " · overdue" : ""}</Badge></td>
                  <td className="px-3 py-2">{r.attempts?.length ? `${r.best_score}% (${r.attempts.length} attempt${r.attempts.length === 1 ? "" : "s"})` : "—"}</td>
                  <td className="px-3 py-2">{r.certificate_no ? <button type="button" onClick={() => cert(r)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`training-record-cert-${r.id}`}><Certificate size={13} /> {r.certificate_no}{r.expires_on ? ` · to ${fmtDate(r.expires_on)}` : ""}</button> : "—"}</td>
                  <td className="px-3 py-2 text-right">{r.status !== "passed" && <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" onClick={() => delRec(r)} data-testid={`training-record-del-${r.id}`}><Trash size={12} /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CourseCreateDialog open={create} onOpenChange={setCreate} onCreated={(c) => { load(); setEditId(c.id); }} />
      <CourseEditorDialog courseId={editId} open={!!editId} onOpenChange={(o) => { if (!o) setEditId(null); }} onSaved={load} />
      <AssignDialog course={assign} open={!!assign} onOpenChange={(o) => { if (!o) setAssign(null); }} onDone={load} />
      {brandingDialog}
    </div>
  );
}
